import { Save } from "@mui/icons-material";
import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { fetchSlaSettings, saveSlaSettings, toSlaApiError, type SlaApiError } from "api";
import { getTranslation } from "common";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import { useTranslations } from "hooks";
import { SLA_MONITOR_TEXT_KEYS } from "model/slaMonitorModel";
import type { SlaSettingsDto, SlaSettingsInput } from "model/slaModel";
import { useCallback, useEffect, useState, type ReactNode } from "react";

/**
 * **Impostazioni SLA di organizzazione** (piano §5 F4c: "disattivazione org").
 *
 * Rotte **realmente esistenti** lato Server, admin-only:
 * `GET|PUT /sla/organizations/{org}/settings` (`Controller.SLA.pas:89-93`,
 * `MVCDoc('Impostazioni SLA di organizzazione (admin-only)')`, modello
 * `SlaSettings.pas:47` colonna `enabled`). L'adattatore client e'
 * `slaApi.ts:375-401`.
 *
 * Perche' lo stato reso e' **quello del Server**: dopo ogni scrittura il pannello
 * *rilegge* (`fetchSlaSettings`) invece di applicare uno stato ottimistico. La
 * regola di F4b vale anche qui — nessun cambio a insaputa dell'utente: l'attributo
 * `data-settings-source` dichiara da dove viene il valore mostrato e
 * `data-sla-enabled` e' sempre la lettura del Server, mai l'intenzione del click.
 *
 * Non si offre nulla a chi non e' amministratore: la rotta e' admin-only, quindi il
 * pannello **dichiara** il vincolo (`sla-statistics-settings-forbidden`, chiave
 * `sla.monitor.forbidden` gia' usata per i 403) invece di mostrare un interruttore
 * che il Server rifiuterebbe.
 *
 * Impostazioni di organizzazione (`showOrganizationFields`, vista impostazioni
 * dell'organizzazione): con lo SLA attivo, sotto l'interruttore, il pannello rende
 * le altre colonne di `sla_settings` che il PUT legge (`Controller.SLA.pas`, ramo
 * httpPUT: `risk_remaining_percent`, `nudge_remaining_percent`,
 * `escalation_enabled`, `digest_enabled`, `min_duration_minutes`,
 * `max_notification_latency_seconds`). `digest_at` non e' letto dal PUT: e'
 * mostrato in sola lettura. La rotta non ha `expected_revision` (l'ultima
 * scrittura vince): dopo il salvataggio il pannello rilegge.
 *
 * Errori del salvataggio: tenuti in uno stato proprio (`saveError`), perche' la
 * rilettura che segue ogni scrittura azzera l'errore di lettura. Un 400 con una
 * chiave `sla_*` (`detailedmessage`) si traduce dal catalogo; altrimenti
 * `sla.settings.save_error`.
 *
 * `children` riceve le impostazioni lette e si rende solo con lo SLA attivo: la
 * vista impostazioni ci monta la configurazione per modello, senza navigare. Con
 * lo SLA disattivo il pannello mostra al suo posto un breve suggerimento.
 */
interface SlaOrganizationSettingsPanelProps {
  idorganization: number;
  /** Ruolo dell'utente nell'organizzazione scelta (rotta admin-only). */
  isAdmin: boolean;
  /** Rende il modulo delle impostazioni di organizzazione sotto l'interruttore. */
  showOrganizationFields?: boolean;
  /** Configurazione resa sotto, solo con lo SLA attivo (lettura del Server). */
  children?: (settings: SlaSettingsDto) => ReactNode;
}

/** Bozza del modulo: stringhe, cosi' un campo vuoto resta modificabile. */
interface SettingsDraft {
  riskremainingpercent: string;
  nudgeremainingpercent: string;
  escalationenabled: boolean;
  digestenabled: boolean;
  mindurationminutes: string;
  maxnotificationlatencyseconds: string;
}

type NumericDraftField =
  | "riskremainingpercent"
  | "nudgeremainingpercent"
  | "mindurationminutes"
  | "maxnotificationlatencyseconds";

const toDraft = (settings: SlaSettingsDto): SettingsDraft => ({
  riskremainingpercent: String(settings.riskremainingpercent ?? ""),
  nudgeremainingpercent: String(settings.nudgeremainingpercent ?? ""),
  escalationenabled: Boolean(settings.escalationenabled),
  digestenabled: Boolean(settings.digestenabled),
  mindurationminutes: String(settings.mindurationminutes ?? ""),
  maxnotificationlatencyseconds: String(settings.maxnotificationlatencyseconds ?? ""),
});

/** Intero nell'intervallo chiuso, altrimenti `null`. */
const parseIntInRange = (value: string, min: number, max: number): number | null => {
  if (!/^\d+$/.test(value.trim())) return null;
  const parsed = Number(value);
  return parsed >= min && parsed <= max ? parsed : null;
};

const TEST_ID = "sla-statistics-settings";

const SlaOrganizationSettingsPanel = ({
  idorganization,
  isAdmin,
  showOrganizationFields = false,
  children,
}: SlaOrganizationSettingsPanelProps) => {
  const translations = useTranslations();
  const [settings, setSettings] = useState<SlaSettingsDto | null>(null);
  const [draft, setDraft] = useState<SettingsDraft | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<SlaApiError | null>(null);
  /** Errore dell'ultimo salvataggio: sopravvive alla rilettura che lo segue. */
  const [saveError, setSaveError] = useState<SlaApiError | null>(null);

  const t = useCallback(
    (key: string): string => getTranslation(translations, key),
    [translations],
  );

  /** Lettura: e' la sola fonte dello stato reso (`data-settings-source`). */
  const load = useCallback(async () => {
    if (!isAdmin) {
      setSettings(null);
      setDraft(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await fetchSlaSettings(idorganization);
      setSettings(response);
      setDraft(response ? toDraft(response) : null);
      setError(null);
    } catch (caught) {
      setSettings(null);
      setDraft(null);
      setError(toSlaApiError(caught));
    } finally {
      setLoading(false);
    }
  }, [idorganization, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  /** Scrittura esplicita, poi rilettura: mai uno stato ottimistico. */
  const write = async (body: SlaSettingsInput) => {
    setLoading(true);
    setSaveError(null);
    try {
      await saveSlaSettings(idorganization, body);
      setError(null);
    } catch (caught) {
      setSaveError(toSlaApiError(caught));
    } finally {
      await load();
    }
  };

  const toggleEnabled = (enabled: boolean) => write({ enabled });

  const risk = draft ? parseIntInRange(draft.riskremainingpercent, 1, 99) : null;
  const nudge = draft ? parseIntInRange(draft.nudgeremainingpercent, 1, 99) : null;
  const minDuration = draft ? parseIntInRange(draft.mindurationminutes, 0, 100000) : null;
  const maxLatency = draft
    ? parseIntInRange(draft.maxnotificationlatencyseconds, 1, 86400)
    : null;
  const draftValid =
    risk !== null && nudge !== null && minDuration !== null && maxLatency !== null;

  /** Impostazioni di organizzazione: stesso PUT, solo i campi che legge. */
  const saveOrganizationFields = () => {
    if (!draft || risk === null || nudge === null || minDuration === null || maxLatency === null) {
      return;
    }
    write({
      risk_remaining_percent: risk,
      nudge_remaining_percent: nudge,
      escalation_enabled: draft.escalationenabled,
      digest_enabled: draft.digestenabled,
      min_duration_minutes: minDuration,
      max_notification_latency_seconds: maxLatency,
    });
  };

  const setDraftField = (patch: Partial<SettingsDraft>) =>
    setDraft((current) => (current ? { ...current, ...patch } : current));

  const numberField = (
    field: NumericDraftField,
    labelKey: string,
    invalid: boolean,
    min: number,
    max: number,
  ) => (
    <Grid item xs={12} sm={6} md={4}>
      <TextField
        id={`${TEST_ID}-${field}`}
        type="number"
        size="small"
        fullWidth
        label={t(labelKey)}
        value={draft?.[field] ?? ""}
        error={invalid}
        disabled={loading}
        inputProps={{ min, max, step: 1 }}
        onChange={({ target }) => setDraftField({ [field]: target.value })}
      />
    </Grid>
  );

  const showBelow = showOrganizationFields || Boolean(children);

  return (
    <Stack
      spacing={1}
      data-testid={`${TEST_ID}-panel`}
      data-sla-admin={isAdmin ? "true" : "false"}
      data-sla-enabled={settings ? String(settings.enabled) : ""}
      data-settings-source="sla/organizations/settings"
    >
      <Typography variant="subtitle1" data-testid={`${TEST_ID}-title`}>
        {t("sla.settings.title")}
      </Typography>

      {!isAdmin && (
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid={`${TEST_ID}-forbidden`}
          data-error-key={SLA_MONITOR_TEXT_KEYS.forbidden}
        >
          {t(SLA_MONITOR_TEXT_KEYS.forbidden)}
        </Typography>
      )}

      {isAdmin && loading && (
        <CircularProgress size={20} data-testid={`${TEST_ID}-loading`} />
      )}

      {isAdmin && error && (
        <Alert
          severity="error"
          data-testid={`${TEST_ID}-error`}
          data-error-status={error.status === null ? "" : String(error.status)}
        >
          {t(
            error.status === 403
              ? SLA_MONITOR_TEXT_KEYS.forbidden
              : SLA_MONITOR_TEXT_KEYS.loadError,
          )}
        </Alert>
      )}

      {isAdmin && saveError && (
        <Alert
          severity="error"
          data-testid={`${TEST_ID}-save-error`}
          data-error-status={saveError.status === null ? "" : String(saveError.status)}
          data-error-key={saveError.messageKey ?? ""}
        >
          {t(
            saveError.messageKey ??
              (saveError.status === 403
                ? SLA_MONITOR_TEXT_KEYS.forbidden
                : "sla.settings.save_error"),
          )}
        </Alert>
      )}

      {isAdmin && settings && (
        <FormControlLabel
          control={
            <Switch
              checked={settings.enabled}
              disabled={loading}
              onChange={({ target }) => toggleEnabled(target.checked)}
              inputProps={{
                "aria-label": t("sla.settings.enabled"),
                // `inputProps` tipizza gli attributi HTML standard: il testid
                // dell'input reale (quello che porta lo stato) va passato senza
                // perdere il tipo, come in `SlaActivitiesSurface.tsx:236-241`.
                ...({ "data-testid": `${TEST_ID}-enabled` } as Record<string, string>),
              }}
            />
          }
          label={
            <Typography variant="body2" data-testid={`${TEST_ID}-enabled-label`}>
              {t("sla.settings.enabled")}
            </Typography>
          }
        />
      )}

      {isAdmin && settings && !settings.enabled && showBelow && (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid={`${TEST_ID}-disabled-hint`}
        >
          {t("sla.settings.disabled_hint")}
        </Typography>
      )}

      {isAdmin && settings?.enabled && showOrganizationFields && draft && (
        <Box data-testid={`${TEST_ID}-organization-fields`} sx={{ pt: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            {t("sla.settings.organization_defaults")}
          </Typography>
          <Grid container spacing={2}>
            {numberField("riskremainingpercent", "sla.settings.risk_percent", risk === null, 1, 99)}
            {numberField("nudgeremainingpercent", "sla.settings.nudge_percent", nudge === null, 1, 99)}
            {numberField("mindurationminutes", "sla.settings.min_duration", minDuration === null, 0, 100000)}
            {numberField(
              "maxnotificationlatencyseconds",
              "sla.settings.max_latency_seconds",
              maxLatency === null,
              1,
              86400,
            )}
            <Grid item xs={12} sm={6} md={4}>
              {/* `digest_at` non e' letto dal PUT: sola lettura. */}
              <TextField
                id={`${TEST_ID}-digestat`}
                size="small"
                fullWidth
                disabled
                label={t("sla.preferences.digest.at")}
                value={(settings.digestat ?? "").slice(0, 5)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0, sm: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={draft.escalationenabled}
                      disabled={loading}
                      onChange={({ target }) => setDraftField({ escalationenabled: target.checked })}
                      inputProps={
                        { "data-testid": `${TEST_ID}-escalationenabled` } as Record<string, string>
                      }
                    />
                  }
                  label={<Typography variant="body2">{t("sla.settings.escalation_enabled")}</Typography>}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={draft.digestenabled}
                      disabled={loading}
                      onChange={({ target }) => setDraftField({ digestenabled: target.checked })}
                      inputProps={
                        { "data-testid": `${TEST_ID}-digestenabled` } as Record<string, string>
                      }
                    />
                  }
                  label={<Typography variant="body2">{t("sla.preferences.digest.enabled")}</Typography>}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sx={{ textAlign: "right" }}>
              <FlowentiButton
                id="btn-save-sla-organization-settings"
                variant="contained"
                size="small"
                startIcon={<Save />}
                disabled={loading || !draftValid}
                onClick={saveOrganizationFields}
              >
                {t("pm.button.save")}
              </FlowentiButton>
            </Grid>
          </Grid>
        </Box>
      )}

      {isAdmin && settings?.enabled && children && (
        <Box data-testid={`${TEST_ID}-configuration`} sx={{ pt: 2 }}>
          {children(settings)}
        </Box>
      )}
    </Stack>
  );
};

export default SlaOrganizationSettingsPanel;
